// =============================================================================
// CardEffectContext — port of EffectContext struct from Cards/EffectContext.cs
//
// C# struct copy semantics → TS plain object. Callers must clone before mutating
// (per anti-pattern §8.10). Hooks are NOT ported here for Group A — they are
// needed only by EffectPipeline (Group E1). Included as optional so the type
// compiles in both phases.
// =============================================================================

/**
 * Hook called before/after an effect is applied. Receives a frozen (Readonly) view of the
 * context — mirrors C# struct copy semantics where Action<EffectContext> hooks receive a copy.
 * Mutations inside the hook do NOT propagate back to the pipeline's live context.
 * To propagate state changes, use the EffectPipeline's return value or a dedicated mutation API.
 */
export type EffectHook = (ctx: Readonly<EffectContext>) => void;
/**
 * Intercept hook. Returns true → effect is intercepted (EffectResult.Intercepted).
 * Receives a frozen (Readonly) view — same struct-copy semantics as EffectHook.
 */
export type InterceptHook = (ctx: Readonly<EffectContext>) => boolean;

/**
 * @TODO(WEB.2 Group E) — `Readonly<T>` is shallow. Nested `cardTags` array
 * and hook arrays are still mutable through hooks. When EffectPipeline lands,
 * either use a DeepReadonly type or pass a frozen-via-deep-clone snapshot.
 */
export interface EffectContext {
  /** Seat index of the player who played the card (the source/drawer). */
  sourceSeat: number;
  /** Seat index of the primary target. May equal sourceSeat for self-targeting effects. */
  victimSeat: number;
  /** Tags on the card driving this resolution (for Immunity / ByCategory matching). */
  cardTags: string[];
  /** The numeric amount the effect attempted to apply (before interception). */
  attemptedAmount: number;
  /**
   * CardInstanceService instance ID of the card driving this resolve. 0 = unset.
   * AuraApply requires this for click-to-discard symmetry.
   */
  cardInstanceId: number;
  /** Resolved target seat list (multi-target effects expand here). */
  targetSeats: number[];
  /** Tile indices for TileGroup-scoped effects. */
  targetTileIndices: number[];
  /** RNG seed for deterministic resolution. Callers should pass from seeded Rng. */
  rngSeed: number;
  /**
   * WEB.3.D.2 — OR-choice player-choice model.
   * When a card has branched effects (any CardEffect has branchId set), the
   * caller must supply this field to declare which branch the player chose.
   * runCard behaviour:
   *   • undefined + card has branches → Skipped (fail-closed; player must choose).
   *   • defined → effects matching this branchId run; effects with no branchId
   *     always run; effects with a non-matching branchId are branch-skipped.
   *   • undefined + card has NO branched effects → all effects run (legacy path).
   */
  chosenBranchId?: string;
  /**
   * WEB.4.E — Reactive trigger recursion guard.
   * Depth 0 = normal resolution. Depth 1 = inside a trigger response.
   * Pipeline skips trigger dispatch when triggerDepth >= MAX_TRIGGER_DEPTH (1).
   * Prevents Counterattack → Counterattack → ... infinite loop.
   * Absent/undefined treated as 0 (normal resolution).
   */
  triggerDepth?: number;
  /** Optional hook called before effect is applied. */
  beforeApplyHook?: EffectHook;
  /** Optional hook called after effect is applied. */
  afterApplyHook?: EffectHook;
  /** Optional hook; returns true → effect is intercepted (EffectResult.Intercepted). */
  onInterceptHook?: InterceptHook;
}

/** Construct a minimal EffectContext for tests / simple resolves. */
export function makeEffectContext(
  sourceSeat: number,
  victimSeat: number,
  overrides?: Partial<Omit<EffectContext, "sourceSeat" | "victimSeat">>,
): EffectContext {
  return {
    sourceSeat,
    victimSeat,
    cardTags: overrides?.cardTags ?? [],
    attemptedAmount: overrides?.attemptedAmount ?? 0,
    cardInstanceId: overrides?.cardInstanceId ?? 0,
    targetSeats: overrides?.targetSeats ?? [],
    targetTileIndices: overrides?.targetTileIndices ?? [],
    rngSeed: overrides?.rngSeed ?? 0,
    ...(overrides?.chosenBranchId !== undefined
      ? { chosenBranchId: overrides.chosenBranchId }
      : {}),
    ...(overrides?.beforeApplyHook !== undefined
      ? { beforeApplyHook: overrides.beforeApplyHook }
      : {}),
    ...(overrides?.afterApplyHook !== undefined
      ? { afterApplyHook: overrides.afterApplyHook }
      : {}),
    ...(overrides?.onInterceptHook !== undefined
      ? { onInterceptHook: overrides.onInterceptHook }
      : {}),
  };
}

/** Shallow clone of an EffectContext (mirrors C# struct copy semantics). */
export function cloneEffectContext(ctx: EffectContext): EffectContext {
  return { ...ctx };
}

// Re-export EffectResult so callers only need to import from this file when convenient.
export type { EffectResult } from "./effect-result.js";
